import { createSlice } from "@reduxjs/toolkit";
import { showLoading, hideLoading } from "react-redux-loading-bar";
import { addListing, getListings } from "../../../api/listingsAPI";
import { ListingStatus, SearchType } from "../../../app/constants";
import { addFilterWithRange } from "../../../app/utils";
import moment from "moment";

export const archivedListingsSlice = createSlice({
  name: "archivedListings",
  initialState: {
    listings: [],
    searchValue: "",
    selectedFilter: {
      status: null,
      startDate: null,
      endDate: null,
    },
    pagination: {},
    page: 0,
    rowsPerPage: 100,
    sort: "desc",
    sortBy: "createdAt",
    successMessage: "",
    errorMessage: "",
    selectedId: null,
    selectedListing: null,
    recreateModalOpen: false,
    filterModalOpen: false,
  },
  reducers: {
    setPage: (state, action) => {
      state.page = action.payload;
    },
    setRowsPerPage: (state, action) => {
      state.rowsPerPage = action.payload;
    },
    setPagination: (state, action) => {
      state.pagination = action.payload;
    },
    setSort: (state, action) => {
      state.sort = action.payload;
    },
    setSortBy: (state, action) => {
      state.sortBy = action.payload;
    },
    setListings: (state, action) => {
      state.listings = action.payload;
    },
    setSearchValue: (state, action) => {
      state.searchValue = action.payload;
    },
    setSelectedFilter: (state, action) => {
      state.selectedFilter = action.payload;
    },
    setSuccessMessage: (state, action) => {
      state.successMessage = action.payload;
    },
    setErrorMessage: (state, action) => {
      state.errorMessage = action.payload;
    },
    setSelectedListing: (state, action) => {
      state.selectedListing = action.payload;
    },
    setSelectedId: (state, action) => {
      state.selectedId = action.payload;
    },
    showRecreateModal: (state, action) => {
      state.recreateModalOpen = action.payload;
    },
    showFilterModal: (state, action) => {
      state.filterModalOpen = action.payload;
    },
  },
});

export const {
  showFilterModal,
  setSelectedFilter,
  setSelectedListing,
  setSelectedId,
  showRecreateModal,
  setSearchValue,
  setPage,
  setRowsPerPage,
  setPagination,
  setSort,
  setSortBy,
  setListings,
  setSuccessMessage,
  setErrorMessage,
} = archivedListingsSlice.actions;

// The function below is called a selector and allows us to select a value from
// the state. Selectors can also be defined inline where they're used instead of
// in the slice file. For example: `useSelector((state) => state.vendor.value)`
export const selectState = (state) => state.archivedListings;

export const handleGetListings = () => async (dispatch, getState) => {
  dispatch(showLoading());
  const state = getState();

  try {
    let filterParamsArray = [];
    const { selectedFilter } = state.archivedListings;
    addFilterWithRange(
      filterParamsArray,
      "createdAt",
      selectedFilter.startDate
        ? moment(selectedFilter.startDate).format("YYYY-MM-DD")
        : null,
      selectedFilter.endDate
        ? moment(selectedFilter.endDate).format("YYYY-MM-DD")
        : null
    );
    let filterParams = filterParamsArray.join(",");

    const response = await getListings(
      state.archivedListings.page,
      state.archivedListings.rowsPerPage,
      state.archivedListings.sort,
      state.archivedListings.sortBy,
      selectedFilter.status
        ? selectedFilter.status
        : `${ListingStatus.TERMINATED},${ListingStatus.REJECTED}`,
      state.archivedListings.searchValue,
      SearchType.DEFAULT,
      filterParams
    );
    const { content, ...pagination } = response.data;

    dispatch(setPagination(pagination));
    dispatch(setListings(content));
    dispatch(setSelectedId(null));
  } catch (error) {
    dispatch(setErrorMessage(error.message));
  } finally {
    dispatch(hideLoading());
  }
};

export const handleRecreateListing = () => async (dispatch, getState) => {
  dispatch(showLoading());
  const state = getState();

  try {
    const payload = {
      additionalInformation:
        state.archivedListings.selectedListing.additionalInformation,
      requestedMonthlyBuyPrice:
        state.archivedListings.selectedListing.requestedMonthlyBuyPrice,
      requestedMonthlyBuyPriceCurrency:
        state.archivedListings.selectedListing.requestedMonthlyBuyPriceCurrency,
      startDate: state.archivedListings.selectedListing.startDate,
      parkingBay: state.archivedListings.selectedListing.parkingBay,
      parkingAreaId: state.archivedListings.selectedListing.parkingAreaId,
      buyPrices: state.archivedListings.selectedListing.buyPrices,
      customer: state.archivedListings.selectedListing.customer,
    };
    await addListing(payload);
    dispatch(showRecreateModal(false));
    dispatch(setSuccessMessage("Successfully recreated listing!"));
    dispatch(handleGetListings());
  } catch (error) {
    dispatch(setErrorMessage(error.message));
  } finally {
    dispatch(hideLoading());
  }
};

export default archivedListingsSlice.reducer;
